import { bind } from 'bind-decorator';
import { ItemType } from 'items/models';
import { ChildItem, LearningObjective } from 'labxchange-client';
import messages from 'library/displayMessages';
import * as React from 'react';
import { Modal } from 'ui/components';
import { WrappedMessage } from 'utils';
import { ClipVideo } from '../ClipVideo';
import { LearningObjectiveEditor } from '../LearningObjectiveEditor';

export interface PathwayChildUpdateProps {
    notes: string;
    learningObjectives: LearningObjective[];
    videoStartTime?: number;
    videoStopTime?: number;
}

interface Props {
    child: ChildItem;
    onClose: () => void;
    onUpdate: (props: PathwayChildUpdateProps) => void;
}

interface State {
    notes: string;
    learningObjectives: LearningObjective[];
    videoTimestampCustomized: boolean;
    startTime: number;
    endTime: number;
    videoDuration?: number;
}

/** Modal for Pathway asset edition */
export class PathwayChildEditModal extends React.PureComponent<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            notes: this.props.child.notes || '',
            learningObjectives: this.props.child.learningObjectives
                || this.props.child.item.metadata.learningObjectives
                || [],
            videoTimestampCustomized: this.props.child.videoStartTime !== undefined,
            startTime: this.props.child.videoStartTime || 0,
            endTime: this.props.child.videoStopTime || 10,
        };
    }

    public render() {

        return (
            <Modal
                onRequestClose={this.props.onClose}
                size='large' showTopBar={true}
                title={
                    <WrappedMessage message={messages.itemEdit} />
                }
                className='pathway-child-edit-modal'
                content={
                    <>
                        {/* If this is a video, there are some video-specific controls: */}
                        {this.props.child.item.metadata.type === ItemType.Video &&
                        <div className='row'>
                            <div className='col-12'>
                                <div className={'time-controls d-flex'}>
                                    <ClipVideo
                                        pathwayChild={this.props.child}
                                        minClippedLength={10}
                                        onTimeUpdate={this.onTimeUpdate}
                                        onShowControls={this.onShowControls}
                                    />
                                </div>
                            </div>
                        </div>
                        }

                        {/* Learning Objective Editor */}
                        <div className='row'>
                            <div className='col-12'>
                                <LearningObjectiveEditor
                                    learningObjectives={this.state.learningObjectives}
                                    onChange={this.updateLearningObjectives}
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div className='row'>
                            <div className='col-12'>
                                <div className='item-notes'>
                                    <label htmlFor='item-notes-input'>
                                        <WrappedMessage message={messages.pathwayChildNotesLabel}/>
                                    </label>
                                    <textarea
                                        id='item-notes-input'
                                        value={this.state.notes}
                                        onChange={this.updateNotes}
                                        rows={6}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                }
                footer={
                    <button className='primary-button' onClick={this.updateChild}>
                        <WrappedMessage message={messages.pathwayChildUpdate}/>
                    </button>
                }
            />
        );
    }

    @bind private updateLearningObjectives(learningObjectives: LearningObjective[]) {
        this.setState({ learningObjectives });
    }

    @bind private updateNotes(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({notes: event.target.value});
    }

    @bind private onTimeUpdate(startTime: number, endTime: number) {
        this.setState({startTime, endTime});
    }

    @bind private onShowControls(videoDuration: number) {
        this.setState({
            videoTimestampCustomized: true,
            videoDuration,
        });
    }

    @bind private getUpdatedTime() {
        if (this.state.videoTimestampCustomized && this.state.videoDuration) {
            if (this.state.startTime !== 0 || this.state.endTime !== this.state.videoDuration) {
                return [this.state.startTime, this.state.endTime];
            }
        }
        return [undefined, undefined];
    }

    @bind private updateChild() {
        const updatedTime = this.getUpdatedTime();
        this.props.onUpdate({
            notes: this.state.notes,
            learningObjectives: this.state.learningObjectives.filter(x => x.description),
            videoStartTime: updatedTime[0],
            videoStopTime: updatedTime[1],
        });
        this.props.onClose();
    }

}
